import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { CheckCircle2, AlertCircle, Calendar, TrendingUp } from 'lucide-react';

const KoraOS = () => {
  const problems = [
    'Ops scattered across WhatsApp, Sheets, and notes',
    'Margin leakage from unclear costing and purchasing',
    'Missed handoffs between teams',
    'Inconsistent execution across projects and events',
  ];

  const brings = [
    'Dashboards for decisions',
    'Workflows for consistent execution',
    'Templates that standardize delivery',
    'Visibility across costs, tasks, and handoffs',
  ];

  const standardizes = [
    'Recipes + costing + margin visibility',
    'Production checklists + prep timelines',
    'Purchasing lists + inventory basics',
    'Execution hub for day-of operations',
    'Dashboards that flag risk before it becomes expensive',
  ];

  const idealFor = [
    'Hospitality & catering teams',
    'Event production companies and planners',
    'Agencies and service businesses',
    'Any operation that depends on workflows, inventory, and delivery',
  ];

  const roadmap = [
    { icon: <CheckCircle2 className="w-6 h-6" />, title: 'Now', desc: 'Execution visibility + operational standardization' },
    { icon: <Calendar className="w-6 h-6" />, title: 'Next', desc: 'Automations + integrations' },
    { icon: <TrendingUp className="w-6 h-6" />, title: 'Later', desc: 'Multi-location + advanced analytics' },
  ];

  const faqs = [
    { q: 'Is Kora only for hospitality?', a: 'No—built to scale across industries, with hospitality as the anchor use case.' },
    { q: 'Is it available now?', a: 'Early access is limited—request access to be considered.' },
  ];

  return (
    <div className="bg-white pt-32 pb-20">
      {/* Hero */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-serif text-5xl md:text-7xl font-bold text-stone-900 mb-6">
            Kora OS
          </h1>
          <p className="text-2xl text-stone-600 mb-6">
            Operating systems for execution—built from real operations at Ciano.
          </p>
          <p className="text-lg text-stone-600 mb-10 leading-relaxed">
            Designed to scale across industries, with a deep foundation in hospitality and event execution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-stone-900 text-white hover:bg-stone-800 px-8 py-6 text-base">
              Request Early Access
            </Button>
            <Button variant="outline" className="border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white px-8 py-6 text-base">
              See the Ciano Workflow
            </Button>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="section-padding px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-6">The Problem Kora Solves</h2>
          <p className="text-lg text-stone-600 mb-8">Most founder-led operations run on scattered tools:</p>
          <div className="space-y-4">
            {problems.map((item, idx) => (
              <div key={idx} className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-terracota flex-shrink-0 mt-1" />
                <p className="text-stone-600">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Kora Brings */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-8">What Kora Brings</h2>
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {brings.map((item, idx) => (
              <div key={idx} className="flex items-start space-x-3">
                <CheckCircle2 className="w-6 h-6 text-terracota flex-shrink-0 mt-1" />
                <p className="text-stone-600">{item}</p>
              </div>
            ))}
          </div>
          
          {/* Platform Preview - Link to real site */}
          <div className="rounded-lg overflow-hidden shadow-2xl border border-stone-200 mt-12">
            <a href="https://ourkora.com" target="_blank" rel="noopener noreferrer" className="block">
              <div className="bg-gradient-to-br from-stone-900 to-stone-800 p-12 text-white text-center">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-lime-400 rounded-lg flex items-center justify-center">
                    <span className="text-stone-900 font-bold text-xl">K</span>
                  </div>
                  <span className="font-serif text-3xl font-bold">Kora OS</span>
                </div>
                <p className="text-2xl font-serif italic mb-4">The system for complex operations.</p>
                <p className="text-lg opacity-80">Clients, projects, events, inventory, marketing, and finance. All connected.</p>
                <div className="mt-8 inline-flex items-center gap-2 bg-lime-400 text-stone-900 px-6 py-3 font-semibold">
                  Try Free →
                </div>
              </div>
            </a>
            <div className="bg-white py-4 px-6 flex items-center justify-between border-t">
              <div>
                <span className="font-semibold text-stone-900">ourkora.com</span>
                <p className="text-sm text-stone-500">Operating system for hospitality & events</p>
              </div>
              <a 
                href="https://ourkora.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-stone-900 text-white px-4 py-2 text-sm font-semibold hover:bg-stone-800 transition-colors"
              >
                Visit Platform →
              </a>
            </div>
          </div>
          
          {/* Modules showcase */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {['Demand Studio', 'Event Suite', 'Project Orchestration', 'Business Clarity'].map((module, idx) => (
              <div key={idx} className="bg-white p-4 text-center border border-stone-200">
                <p className="text-sm font-semibold text-stone-700">{module}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ciano Use Case */}
      <section className="section-padding px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-6">Ciano Use Case (why Kora exists)</h2>
          <p className="text-lg text-stone-600 mb-8 leading-relaxed">
            Kora was born from the reality of running an experience-driven business—where execution has to be flawless and margins must be protected.
          </p>
          
          <h3 className="font-serif text-2xl font-bold text-stone-900 mb-6">What Kora Standardizes</h3>
          <div className="space-y-4">
            {standardizes.map((item, idx) => (
              <div key={idx} className="flex items-start space-x-3">
                <span className="w-2 h-2 bg-terracota rounded-full mt-2 flex-shrink-0"></span>
                <p className="text-stone-600">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-8">Who It's For</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {idealFor.map((item, idx) => (
              <div key={idx} className="flex items-start space-x-3">
                <span className="w-2 h-2 bg-terracota rounded-full mt-2 flex-shrink-0"></span>
                <p className="text-stone-600">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="section-padding px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-12 text-center">Now / Next / Later</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {roadmap.map((item, idx) => (
              <Card key={idx} className="border-perla text-center">
                <CardContent className="p-8">
                  <div className="flex justify-center mb-4 text-terracota">{item.icon}</div>
                  <h3 className="font-serif text-2xl font-bold text-stone-900 mb-3">{item.title}</h3>
                  <p className="text-stone-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-12 text-center">FAQ</h2>
          <div className="space-y-8">
            {faqs.map((faq, idx) => (
              <div key={idx}>
                <h3 className="font-semibold text-lg text-stone-900 mb-2">{faq.q}</h3>
                <p className="text-stone-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="section-padding px-6 bg-stone-900 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6">
            If execution is your brand, Kora is your system.
          </h2>
          <p className="text-lg mb-8 opacity-90">Response within 24–48 hours</p>
          <Link to="/contact">
            <Button className="bg-white text-stone-900 hover:bg-stone-100 px-12 py-6 text-lg font-semibold">
              Request Early Access
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default KoraOS;
